import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { collection, collectionGroup, getDocs, orderBy, query } from "firebase/firestore";
import { db } from "../../../config/firebaseConfig";
import { ClipboardDocumentIcon } from "@heroicons/react/24/outline";
import { useToast } from "../../../util/ToastProvider";



type PaymentRow = any;

export default function PaymentList() {
  const nav = useNavigate();
  const toast = useToast();

  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("Most recent purchase first");
  const [page, setPage] = useState(1);         // ← ADDED
  const pageSize = 10;                         // ← ADDED

  const [payments, setPayments] = useState<PaymentRow[]>([]); // ← FIXED

  useEffect(() => {
    async function load() {
      const result = await getAllPayments();
  
      const succeededOnly = result.filter(
        (item) => item.paymentStatus === "succeeded"
      );
  
      setPayments(succeededOnly);
    }
  
    load();
  }, []);

  async function getAllPayments(): Promise<any[]> {
    try {
        // 1. Get all payment_sessions
        const sessionsSnap = await getDocs(collection(db, "payment_sessions"));
    
        const sessions = sessionsSnap.docs.map((doc) => ({
          sessionId: doc.id,
        }));
    
        // 2. For each session, get history collection
        const results: any[] = [];
    
        await Promise.all(
          sessions.map(async ({ sessionId }) => {
            const historyRef = collection(db, `payment_sessions/${sessionId}/history`);
            const historySnap = await getDocs(historyRef);
    
            const historyItems = historySnap.docs.map((hDoc) => ({
              id: hDoc.id,
              sessionId,
              ...hDoc.data(),
            }));
    
            results.push(...historyItems);
          })
        );
        console.log("✅ All payment history:", results);
        return results;
      } catch (error: any) {
        console.error("❌ Error fetching payment sessions:", error.message);
        throw error;
      }
  }

  const filteredAndSorted = useMemo(() => {
    let data = payments.filter((p) =>
        (p.metadata?.name || "").toLowerCase().includes(search.toLowerCase())
    );

    switch (sort) {
      case "Most recent purchase first":
        data = [...data].sort(
          (a, b) => b.completedAt.seconds - a.completedAt.seconds
        );
        break;
    
      case "Earliest purchase first":
        data = [...data].sort(
          (a, b) => a.completedAt.seconds - b.completedAt.seconds
        );
        break;
    
      case "Sort by name, A-Z":
        data = [...data].sort((a, b) =>
          a.metadata?.name.localeCompare(b.metadata?.name)
        );
        break;
    
      case "Sort by name, Z-A":
        data = [...data].sort((a, b) =>
          b.metadata?.name.localeCompare(a.metadata?.name)
        );
        break;
    
      case "Sort by report title, A-Z":
        data = [...data].sort((a, b) =>
          a.metadata?.itemTitle.localeCompare(b.metadata?.itemTitle)
        );
        break;
    
      case "Sort by report title, Z-A":
        data = [...data].sort((a, b) =>
          b.metadata?.itemTitle.localeCompare(a.metadata?.itemTitle)
        );
        break;
    }
    

    return data;
  }, [search, sort, payments]);

  const totalPages = Math.ceil(filteredAndSorted.length / pageSize); // ← ADDED

  const paginatedData = filteredAndSorted.slice( // ← ADDED
    (page - 1) * pageSize,
    page * pageSize
  );

  function formatTimestamp(seconds: number) {
    const date = new Date(seconds * 1000);
  
    const day = String(date.getDate()).padStart(2, "0");
  
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = monthNames[date.getMonth()];
  
    const year = date.getFullYear();
  
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
  
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12; // 0 → 12
  
    return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
  }
  
 
  
  return (
    <div className="mx-8 my-6">
      <h1 className="text-xl font-semibold mb-6">Payments</h1>

      {/* Search + Filter */}
      <div className="flex gap-3 mb-6 items-center">
        {/* Search Input */}
        <div className="flex-1 relative">
          {/* <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            <svg className="h-5 w-5" viewBox="0 0 24 24" stroke="currentColor" fill="none" strokeWidth={2}>
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
          </span> */}
          <input
            type="text"
            placeholder="Search by member's name..."
            className="w-full h-[44px] px-4 py-2 border border-gray-300 rounded-md
             focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400
             placeholder:text-[rgba(106,114,130,1)]
             text-gray-800 transition-all duration-200 rounded-lg"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Filter Dropdown */}
        <div className="w-[300px]">
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="w-full h-[44px] px-4 pr-10 border border-[#D0D5DD] rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1] appearance-none cursor-pointer"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23667085' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 12px center'
            }}
          >
            <option>Most recent purchase first</option>
            <option>Earliest purchase first</option>
            <option>Sort by name, A-Z</option>
            <option>Sort by name, Z-A</option>
            <option>Sort by report title, A-Z</option>
            <option>Sort by report title, Z-A</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-gray-600 font-medium">
            <tr>
              <th className="px-5 py-3 text-left">Member name</th>
              {/* <th className="px-5 py-3 text-left"></th> */}
              <th className="px-5 py-3 text-left">Report title</th>
              <th className="px-5 py-3 text-left">Amount paid</th>
              <th className="px-5 py-3 text-left">Purchased on</th>
              <th className="px-5 py-3 text-left">Stripe transaction ID</th>
              <th className="px-5 py-3 text-left">Receipt</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {paginatedData.map((p) => (
              <tr key={p.id} className="hover:bg-gray-50">
                <td
                  className="px-5 truncate max-w-[30ch] py-3 text-brand-600 cursor-pointer hover:underline max-w-[250px] truncate"
                  onClick={() => nav(`/admin/members/view/${p?.metadata?.userId}`)}
                >
                  {p.metadata?.name || "--"}
                </td>
                <td className="px-5 py-3 text-gray-800 truncate max-w-[30ch]">{p.metadata?.itemTitle}</td>
                <td className="px-5 py-3 max-w-[100px]">$ {p.amount}</td>
                <td className="px-5 py-3 text-gray-800">{formatTimestamp(p.completedAt.seconds)}</td>
                <td className="px-5 py-3 text-gray-800 flex items-center gap-2">
                    {p.paymentData?.paymentIntentId ? (
                      <>
                        {p.paymentData.paymentIntentId}
                        <ClipboardDocumentIcon
                          onClick={() => {
                            navigator.clipboard.writeText(p.paymentData.paymentIntentId);
                            toast.info("Copied to clipboard!");  // HERE!
                          }}
                          className="h-4 w-4 text-brand-600 cursor-pointer hover:text-brand-700"
                        />
                      </>
                    ) : (
                      "--"
                    )}
                  </td>
                <td className="px-5 py-3">
                {p.paymentData?.receiptUrl ? (
                    <a
                    href={p.paymentData.receiptUrl}
                    target="_blank"
                    className="text-brand-600 hover:underline text-sm"
                    rel="noopener noreferrer"
                    >
                    View
                    </a>
                ) : (
                    "--"
                )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="flex justify-center items-center py-4 px-5 text-gray-600 text-sm">
          <button
            onClick={() => setPage((p) => Math.max(p - 1, 1))}
            disabled={page === 1}
            className={page === 1 ? "opacity-40 cursor-not-allowed" : "text-brand-600"}
          >
            ← Previous
          </button>

          <div className="flex gap-2">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                onClick={() => setPage(n)}
                className={`px-2 ${page === n ? "text-brand-600 font-semibold" : ""}`}
              >
                {n}
              </button>
            ))}
          </div>

          <button
            onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
            disabled={page === totalPages}
            className={page === totalPages ? "opacity-40 cursor-not-allowed" : "text-brand-600"}
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}

